import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowRight,
  Check,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock,
  GraduationCap,
  Handshake,
  Lock,
  type LucideIcon,
  Mic,
  Play,
  Scissors,
  Search,
  Sparkles,
  Sun,
  Video,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { toast } from '@/components/ui/sonner'
import {
  useGetAcademyProgressQuery,
  useReportAcademyEventMutation,
} from '@/redux/collaborationsApi'
import { useAcademyCatalogue } from '@/hooks/useAcademyCatalogue'
import {
  ACADEMY_MAX_POINTS,
  SUGGESTED_PROMPTS,
  askAcademy,
  currentStage,
  fmtDuration,
  isNew,
  lessonAction,
  lessonById,
  nextLesson,
  positionInTrack,
  stageCount,
  trackByKey,
  trackStatuses,
  type AcademyAnswer,
  type Catalogue,
  type Lesson,
  type Progress,
  type TrackKey,
} from '@/lib/academy'

const TRACK_ICONS: Record<string, LucideIcon> = {
  basics: Sparkles,
  lighting: Sun,
  sound: Mic,
  editing: Scissors,
  'on-camera': Video,
  'brand-deals': Handshake,
}

const EMPTY: Progress = {}

/** A track's icon; tracks the team adds get the Academy cap */
const iconFor = (key: TrackKey): LucideIcon => TRACK_ICONS[key] ?? GraduationCap

/**
 * DEMO ONLY. "Ask the Academy" is not an assistant: askAcademy() matches keywords against about
 * a dozen canned replies (packages/common/utils/academy.ts). It is hidden unless
 * VITE_FEATURE_ASK_ACADEMY=true (set in .env.dev and .env.marketplace, never production). Replace
 * it with a real model-backed answer before turning the flag on anywhere real athletes sign in.
 */
const askEnabled = import.meta.env.VITE_FEATURE_ASK_ACADEMY === 'true'

/** The library once the team has published a lesson; until then, the outline of what's coming */
const Academy: React.FC = () => {
  const { data, isLoading } = useGetAcademyProgressQuery()
  if (isLoading) return null
  return data?.catalogue.lessons.length ? (
    <AcademyLibrary />
  ) : (
    <AcademyComingSoon outline={data?.outline} />
  )
}

/**
 * Before the first lessons are recorded: what the Academy is, its four stages and the tracks in
 * each, and something useful to do meanwhile.
 */
const AcademyComingSoon: React.FC<{ outline?: Pick<Catalogue, 'stages' | 'tracks'> }> = ({
  outline,
}) => {
  const stages = outline?.stages ?? {}
  const tracks = outline?.tracks ?? []
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 pb-10 pt-1">
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#EDE9FE]">
          <GraduationCap className="h-6 w-6 text-[#6D28D9]" />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold leading-tight text-foreground">Academy</h1>
          <p className="text-sm text-muted-foreground">
            Short how-tos on the creator skills brands pay for
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <span className="inline-flex rounded-full bg-[#EDE9FE] px-2.5 py-0.5 text-xs font-bold text-[#6D28D9]">
          Coming soon
        </span>
        <h2 className="mt-2 text-xl font-bold text-foreground">
          The first lessons are on the way
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Short videos, stage by stage. Each one ends with a single thing to do in the app, so you
          put it to work right away. We'll let you know when they drop.
        </p>
      </div>

      <section>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          What you'll learn
        </h2>
        <ol className="space-y-2">
          {Array.from({ length: Math.max(0, ...tracks.map((t) => t.stage)) }, (_, i) => i + 1).map(
            (stage) => (
              <li key={stage} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wider text-[#6D28D9]">
                  Stage {stage} · {stages[stage] ?? ''}
                </p>
                <ul className="mt-2 space-y-2">
                  {tracks
                    .filter((t) => t.stage === stage)
                    .map((t) => {
                      const Icon = iconFor(t.key)
                      return (
                        <li key={t.key} className="flex items-start gap-3">
                          <span
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl"
                            style={{ background: t.tint, color: t.ink }}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0">
                            <span className="block font-semibold text-foreground">{t.label}</span>
                            <span className="block text-sm text-muted-foreground">{t.blurb}</span>
                          </span>
                        </li>
                      )
                    })}
                </ul>
              </li>
            ),
          )}
        </ol>
      </section>

      <Link
        to="/calendar"
        className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card px-4 py-3 shadow-sm hover:bg-muted/40"
      >
        <span className="min-w-0">
          <span className="flex items-center gap-2 font-semibold text-foreground">
            <CalendarDays className="h-4 w-4 text-[#DB2777]" /> Meanwhile, plan this week's posts
          </span>
          <span className="block text-sm text-muted-foreground">
            Two posts a week keeps your content consistent
          </span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </Link>
    </div>
  )
}

/**
 * Creator Academy: the athlete's how-to library. The player sits on top and the library below;
 * "Ask the Academy" turns a question ("my audio echoes") into lessons. Stages unlock in order.
 * Other screens and notifications open a lesson with /academy?v=<lessonId>.
 */
const AcademyLibrary: React.FC = () => {
  const { refreshUser } = useAuth()
  const [params, setParams] = useSearchParams()
  const { data: academy, isSuccess } = useGetAcademyProgressQuery()
  const [report] = useReportAcademyEventMutation()
  const cat = useAcademyCatalogue()
  const { lessons: LESSONS, tracks: TRACKS } = cat
  const progress: Progress = academy?.progress ?? EMPTY
  const [currentId, setCurrentId] = useState<string>(() => {
    const v = params.get('v')
    return v && lessonById(cat, v) ? v : nextLesson(cat, {})!.id
  })
  const [season, setSeason] = useState<TrackKey>(
    () => lessonById(cat, currentId)?.track ?? TRACKS[0].key,
  )
  const [query, setQuery] = useState('')
  const [answer, setAnswer] = useState<AcademyAnswer | null>(null)
  const [started, setStarted] = useState(false)
  const [dismissedNew, setDismissedNew] = useState(false)
  const [listening, setListening] = useState(false)
  const recogRef = useRef<{ stop: () => void } | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const playerRef = useRef<HTMLDivElement>(null)

  // Once progress loads, open on where they are (unless a link chose the lesson)
  const placed = useRef(!!params.get('v'))
  useEffect(() => {
    if (!isSuccess || placed.current) return
    placed.current = true
    const next = nextLesson(cat, progress)
    if (!next) return
    setCurrentId(next.id)
    setSeason(next.track)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess])

  // A link elsewhere in the app can change ?v= while the page is open
  const v = params.get('v')
  useEffect(() => {
    if (v && lessonById(cat, v) && v !== currentId) {
      setCurrentId(v)
      setSeason(lessonById(cat, v)!.track)
      setStarted(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v])

  const current = lessonById(cat, currentId) ?? LESSONS[0]
  const currentTrack = trackByKey(cat, current.track)!
  const currentProg = progress[current.id]
  const CurrentIcon = iconFor(current.track)
  const currentAction = lessonAction(current)
  const currentCredit = (academy as any)?.lessons?.find((l: any) => l.lessonId === current.id)

  // Tell the server what the player did; it decides when a lesson is watched through
  const lastSent = useRef(0)
  const send = async (event: 'play' | 'progress' | 'pause' | 'ended' | 'done') => {
    const el = videoRef.current
    lastSent.current = Date.now()
    try {
      const res = await report({
        lessonId: current.id,
        event,
        position: el?.currentTime ?? 0,
        duration: el && Number.isFinite(el.duration) ? el.duration : undefined,
      }).unwrap()
      if (res.earned) {
        // Watched through: the lesson is finished once its action is done too
        const action = lessonAction(current)
        const credit = (res as any).lessons?.find((l: any) => l.lessonId === current.id)
        toast(!action ? 'Lesson watched' : credit?.earned ? 'Lesson complete' : 'Lesson watched', {
          description: !action
            ? 'Pick the next one when you are ready.'
            : credit?.earned
              ? `You'd already done it: ${action.label.toLowerCase()}.`
              : `Now put it to work: ${action.label.toLowerCase()}.`,
        })
        refreshUser()
      }
    } catch {
      // Progress catches up on the next report
    }
  }

  const play = (l: Lesson) => {
    setCurrentId(l.id)
    setSeason(l.track)
    setStarted(false)
    setParams({ v: l.id }, { replace: true })
    playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Resume where they left off
  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    const pos = progress[current.id]?.position ?? 0
    const onLoaded = () => {
      if (pos > 0 && pos < current.durationSec - 5) el.currentTime = pos
    }
    el.addEventListener('loadedmetadata', onLoaded)
    return () => el.removeEventListener('loadedmetadata', onLoaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.id])

  // A progress report every 10 seconds of playback
  const onTimeUpdate = () => {
    if (Date.now() - lastSent.current >= 10_000) send('progress')
  }

  const markDone = () => send('done')

  const startPlayback = () => {
    setStarted(true)
    setTimeout(() => videoRef.current?.play().catch(() => undefined), 0)
  }

  const upNext = useMemo(() => nextLesson(cat, progress, current)!, [cat, progress, current])
  const statuses = useMemo(() => trackStatuses(cat, progress), [cat, progress])
  const stage = useMemo(() => currentStage(cat, progress), [cat, progress])
  const doneCount = LESSONS.filter((l) => progress[l.id]?.completed).length

  const ask = (prompt: string) => {
    if (prompt.trim()) setAnswer(askAcademy(cat, prompt))
  }
  const clearAsk = () => {
    setQuery('')
    setAnswer(null)
  }

  // Voice, where the browser has speech recognition (Chrome, Safari)
  type Recog = {
    lang: string
    interimResults: boolean
    onresult: (e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void
    onend: () => void
    onerror: () => void
    start: () => void
    stop: () => void
  }
  const SpeechCtor =
    (window as any).SpeechRecognition ?? ((window as any).webkitSpeechRecognition as unknown)
  const toggleListening = () => {
    if (listening) {
      recogRef.current?.stop()
      return
    }
    if (!SpeechCtor) return
    const r: Recog = new (SpeechCtor as new () => Recog)()
    r.lang = 'en-US'
    r.interimResults = true
    r.onresult = (e) =>
      setQuery(
        Array.from(e.results)
          .map((res) => res[0].transcript)
          .join(' '),
      )
    r.onend = () => {
      setListening(false)
      recogRef.current = null
      setQuery((q) => {
        if (q.trim()) ask(q)
        return q
      })
    }
    r.onerror = () => setListening(false)
    recogRef.current = r
    setListening(true)
    r.start()
  }

  const newDrop = useMemo(
    () => [...LESSONS].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1)).find(isNew),
    [LESSONS],
  )
  const showNew =
    newDrop && !dismissedNew && !progress[newDrop.id]?.completed && newDrop.id !== current.id

  const seasonStatus = statuses.find((s) => s.track.key === season)!
  const seasonLocked = seasonStatus.track.stage > stage
  const nextSeason = TRACKS[TRACKS.findIndex((t) => t.key === season) + 1]
  const lessonsInTrack = LESSONS.filter((l) => l.track === current.track).length

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 pb-10 pt-1">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#EDE9FE]">
          <GraduationCap className="h-6 w-6 text-[#6D28D9]" />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold leading-tight text-foreground">Academy</h1>
          <p className="text-sm text-muted-foreground">
            Short how-tos on the creator skills brands pay for
          </p>
        </div>
      </div>

      {/* Where they are */}
      <div className="rounded-2xl border border-border bg-card px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 text-sm">
            <span className="text-xs font-bold uppercase tracking-wider text-[#6D28D9]">
              Stage {stage} of {stageCount(cat)}
            </span>{' '}
            <span className="font-semibold text-foreground">{cat.stages[stage]}</span>
          </p>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {doneCount}/{LESSONS.length} lessons
          </span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-[#84CC16] transition-all"
            style={{ width: `${Math.round((doneCount / LESSONS.length) * 100)}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {(academy?.points ?? 0) >= ACADEMY_MAX_POINTS ? (
            <>
              <span className="font-semibold text-[#3F7A0A]">Nice work.</span> Keep watching to
              sharpen your content.
            </>
          ) : (
            <>
              <span className="font-semibold text-foreground">
                Watch a lesson to the end, then do what it asks
              </span>{' '}
              to put it to work.
            </>
          )}
        </p>
      </div>

      {/* A new lesson (what the notification lands on) */}
      {showNew && newDrop ? (
        <div className="flex items-center gap-3 rounded-2xl border border-[#C9E9A6] bg-[#F7FEE7] px-4 py-3">
          <Sparkles className="h-4 w-4 shrink-0 text-[#3F7A0A]" />
          <button type="button" onClick={() => play(newDrop)} className="min-w-0 flex-1 text-left">
            <span className="block text-xs font-bold uppercase tracking-wider text-[#3F7A0A]">
              New this week
            </span>
            <span className="block truncate text-sm font-semibold text-foreground">
              {newDrop.title}
              <span className="font-normal text-muted-foreground">
                {' '}
                · {fmtDuration(newDrop.durationSec)}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => play(newDrop)}
            className="inline-flex shrink-0 items-center gap-0.5 text-sm font-bold text-[#3F7A0A]"
          >
            Watch <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setDismissedNew(true)}
            className="shrink-0 p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      {/* Player */}
      <div
        ref={playerRef}
        className="scroll-mt-20 overflow-hidden rounded-2xl border border-border bg-[#0F172A] shadow-sm"
      >
        <div className="relative aspect-video">
          <video
            key={current.id}
            ref={videoRef}
            src={current.src}
            poster={current.poster ?? undefined}
            controls={started}
            playsInline
            preload="metadata"
            onTimeUpdate={onTimeUpdate}
            onPlay={() => {
              setStarted(true)
              send('play')
            }}
            onPause={(e) => {
              if (!e.currentTarget.ended) send('pause')
            }}
            onEnded={() => send('ended')}
            className="absolute inset-0 h-full w-full"
          />
          {!started ? (
            <button
              type="button"
              onClick={startPlayback}
              aria-label={`Play ${current.title}`}
              className="group absolute inset-0 flex flex-col items-center justify-center p-4 text-center"
              style={{
                background: `linear-gradient(135deg, ${currentTrack.color} 0%, #0F172A 85%)`,
              }}
            >
              <CurrentIcon className="absolute left-4 top-4 h-5 w-5 text-[#FFFFFF]/80" />
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FFFFFF] shadow-xl transition-transform group-hover:scale-105">
                <Play className="ml-1 h-7 w-7 text-[#0F172A]" fill="currentColor" />
              </span>
              <span className="mt-3 text-xs font-semibold tracking-wide text-[#FFFFFF]/90">
                {(currentProg?.position ?? 0) > 0 && !currentProg?.completed
                  ? `Resume · ${fmtDuration(currentProg!.position)} in`
                  : `Tap to play · ${fmtDuration(current.durationSec)}`}
              </span>
            </button>
          ) : null}
        </div>
      </div>

      {/* Now playing */}
      <div>
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <span
            className="rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider"
            style={{ color: currentTrack.ink, background: currentTrack.tint }}
          >
            {currentTrack.label} · {positionInTrack(cat, current)} of {lessonsInTrack}
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {fmtDuration(current.durationSec)}
          </span>
          {isNew(current) ? (
            <span className="rounded-full bg-[#C0FD72] px-2 py-0.5 text-xs font-bold text-[#0F172A]">
              NEW
            </span>
          ) : null}
        </div>
        <h2 className="text-xl font-bold leading-snug text-foreground">{current.title}</h2>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{current.description}</p>
        <div className="mt-3">
          {currentProg?.watched ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#3F7A0A]">
              <CheckCircle2 className="h-4 w-4" /> Watched
            </span>
          ) : currentProg?.completed ? (
            <span className="text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                <CheckCircle2 className="h-4 w-4" /> Marked done
              </span>{' '}
              · watch it through to finish it
            </span>
          ) : (
            <button
              type="button"
              onClick={markDone}
              className="inline-flex h-10 items-center gap-1.5 rounded-full border border-border bg-card px-4 text-sm font-semibold text-foreground hover:bg-muted"
            >
              <Check className="h-4 w-4" /> Mark as done
            </button>
          )}
        </div>
        {currentAction ? (
          // The lesson's action: the one thing to do in the app once it's watched
          <Link
            to={currentAction.to}
            className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-border bg-muted/40 px-4 py-3 hover:bg-muted"
          >
            <span className="min-w-0">
              <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {currentCredit?.earned ? 'Done' : 'After this lesson'}
              </span>
              <span className="block font-semibold text-foreground">{currentAction.label}</span>
            </span>
            {currentCredit?.earned ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-[#3F7A0A]" />
            ) : (
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
          </Link>
        ) : null}
      </div>

      {/* Up next */}
      {upNext.id !== current.id ? (
        <button
          type="button"
          onClick={() => play(upNext)}
          className="flex items-center gap-3 rounded-2xl border border-border bg-card p-2.5 text-left shadow-sm transition-colors hover:bg-muted/50"
        >
          <Thumb lesson={upNext} className="w-24" />
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Up next
            </span>
            <span className="line-clamp-2 block text-sm font-semibold leading-snug text-foreground">
              {upNext.title}
            </span>
            <span className="block text-xs text-muted-foreground">
              {trackByKey(cat, upNext.track)?.label} · {fmtDuration(upNext.durationSec)}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      ) : null}

      {askEnabled ? (
        <>
          {/* Ask the Academy (demo only, see askEnabled) */}
          <div className="rounded-2xl border border-[#DDD0FC] bg-[#F8F5FF] p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#6D28D9]">
              <Sparkles className="h-3.5 w-3.5" /> Ask the Academy
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                ask(query)
              }}
              className="relative"
            >
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={listening ? 'Listening…' : 'What are you stuck on?'}
                aria-label="Ask the Academy"
                className="h-12 w-full rounded-xl border border-border bg-card pl-9 pr-28 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-[#8B5CF6] focus:ring-4 focus:ring-[#8B5CF6]/15"
              />
              <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-1">
                {query ? (
                  <button
                    type="button"
                    onClick={clearAsk}
                    aria-label="Clear"
                    className="flex h-9 w-8 items-center justify-center text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
                {SpeechCtor ? (
                  <button
                    type="button"
                    onClick={toggleListening}
                    aria-label={listening ? 'Stop listening' : 'Say your question'}
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-lg',
                      listening
                        ? 'animate-pulse bg-[#E11D48] text-[#FFFFFF]'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <Mic className="h-4 w-4" />
                  </button>
                ) : null}
                <button
                  type="submit"
                  disabled={!query.trim()}
                  className="flex h-9 items-center gap-1 rounded-lg bg-[#0F172A] px-3 text-xs font-bold text-[#FFFFFF] disabled:opacity-30"
                >
                  Ask <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </form>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Try:</span>
              {SUGGESTED_PROMPTS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setQuery(s)
                    ask(s)
                  }}
                  className="rounded-full border border-border bg-card px-2.5 py-1 text-xs font-semibold text-foreground hover:border-[#8B5CF6]"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {/* Library: their answer, or the tracks */}
      {answer ? (
        <section className="space-y-3">
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#EDE9FE]">
              <Sparkles className="h-3.5 w-3.5 text-[#6D28D9]" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-relaxed text-foreground">{answer.reply}</p>
              {answer.lessons.length ? (
                <p className="text-xs text-muted-foreground">
                  {answer.lessons.length} {answer.lessons.length === 1 ? 'lesson' : 'lessons'} ·
                  tap one to play it
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={clearAsk}
              className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" /> All lessons
            </button>
          </div>
          <div className="space-y-2">
            {answer.lessons.map((l) => (
              <LessonRow
                key={l.id}
                lesson={l}
                progress={progress[l.id]}
                active={l.id === current.id}
                onPlay={play}
              />
            ))}
          </div>
        </section>
      ) : (
        <section className="space-y-3">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Tracks
          </p>
          <div className="grid grid-cols-2 gap-2">
            {statuses.map(({ track: t, done, total }) => {
              const locked = t.stage > stage
              const Icon = iconFor(t.key)
              const on = t.key === season
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setSeason(t.key)}
                  aria-pressed={on}
                  className={cn(
                    'flex min-w-0 items-center gap-2.5 rounded-2xl border bg-card p-2.5 text-left shadow-sm transition',
                    on ? 'border-[#84CC16] ring-2 ring-[#C0FD72]' : 'border-border',
                  )}
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                    style={{ background: t.tint }}
                  >
                    <Icon className="h-4 w-4" style={{ color: t.ink }} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold leading-tight text-foreground">
                      {t.label}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {locked ? `Stage ${t.stage}` : `${done}/${total} done`}
                    </span>
                  </span>
                  {done === total ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#3F7A0A]" />
                  ) : locked ? (
                    <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  ) : null}
                </button>
              )
            })}
          </div>

          <div className="pt-1">
            <p className="text-lg font-bold text-foreground">{seasonStatus.track.label}</p>
            <p className="text-sm text-muted-foreground">{seasonStatus.track.blurb}</p>
          </div>
          {seasonLocked ? (
            <p className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5 shrink-0" />
              Unlocks when you finish stage {seasonStatus.track.stage - 1}. You can still watch a
              lesson.
            </p>
          ) : null}
          <div className="space-y-2">
            {seasonStatus.lessons.map((l) => (
              <LessonRow
                key={l.id}
                lesson={l}
                progress={progress[l.id]}
                active={l.id === current.id}
                locked={seasonLocked}
                onPlay={play}
              />
            ))}
          </div>

          {nextSeason ? (
            <button
              type="button"
              onClick={() => setSeason(nextSeason.key)}
              className="flex w-full items-center justify-between gap-3 rounded-2xl border border-dashed border-border px-4 py-3 text-left hover:bg-muted/40"
            >
              <span className="min-w-0">
                <span className="block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Next track
                </span>
                <span className="block text-sm font-semibold text-foreground">
                  {nextSeason.label}
                  <span className="font-normal text-muted-foreground">
                    {nextSeason.stage > stage
                      ? ` · unlocks after stage ${nextSeason.stage - 1}`
                      : ` · stage ${nextSeason.stage}`}
                  </span>
                </span>
              </span>
              {nextSeason.stage > stage ? (
                <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
            </button>
          ) : null}
        </section>
      )}
    </div>
  )
}

/** A lesson's thumbnail: its poster frame, or the track's colour and icon */
const Thumb: React.FC<{
  lesson: Lesson
  className?: string
  progress?: { completed: boolean; position: number }
}> = ({ lesson, className, progress }) => {
  const cat = useAcademyCatalogue()
  const t = trackByKey(cat, lesson.track)!
  const Icon = iconFor(lesson.track)
  const pct = progress?.completed
    ? 100
    : progress?.position
      ? Math.min(99, Math.round((progress.position / lesson.durationSec) * 100))
      : 0
  return (
    <span
      className={cn('relative block aspect-video shrink-0 overflow-hidden rounded-xl', className)}
      style={{ background: `linear-gradient(135deg, ${t.tint} 0%, ${t.color} 140%)` }}
    >
      {lesson.poster ? (
        <img src={lesson.poster} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center">
          <Icon className="h-6 w-6" style={{ color: t.ink }} />
        </span>
      )}
      <span className="absolute bottom-1 right-1 rounded bg-[#0F172A]/75 px-1.5 py-0.5 text-xs font-semibold text-[#FFFFFF]">
        {fmtDuration(lesson.durationSec)}
      </span>
      {progress?.completed ? (
        <span className="absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#C0FD72]">
          <Check className="h-3 w-3 text-[#0F172A]" strokeWidth={3} />
        </span>
      ) : null}
      {pct > 0 && pct < 100 ? (
        <span className="absolute inset-x-0 bottom-0 h-1 bg-[#FFFFFF]/50">
          <span className="block h-full" style={{ width: `${pct}%`, background: t.ink }} />
        </span>
      ) : null}
    </span>
  )
}

const LessonRow: React.FC<{
  lesson: Lesson
  progress?: { completed: boolean; position: number }
  active: boolean
  locked?: boolean
  onPlay: (l: Lesson) => void
}> = ({ lesson, progress, active, locked, onPlay }) => {
  const cat = useAcademyCatalogue()
  const t = trackByKey(cat, lesson.track)!
  const pct = progress?.position
    ? Math.min(99, Math.round((progress.position / lesson.durationSec) * 100))
    : 0
  return (
    <button
      type="button"
      onClick={() => onPlay(lesson)}
      className={cn(
        'flex w-full items-center gap-3 rounded-2xl border bg-card p-2 text-left shadow-sm transition-colors',
        active ? 'border-[#84CC16] bg-[#F7FEE7]' : 'border-border hover:bg-muted/40',
        locked && 'opacity-60',
      )}
    >
      <Thumb lesson={lesson} progress={progress} className="w-28 sm:w-40" />
      <span className="min-w-0 flex-1 py-0.5">
        <span className="mb-0.5 flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: t.ink }}>
            {t.label} · {positionInTrack(cat, lesson)}
          </span>
          {isNew(lesson) && !progress?.completed ? (
            <span className="rounded bg-[#C0FD72] px-1.5 text-xs font-bold text-[#0F172A]">
              NEW
            </span>
          ) : null}
          {active ? (
            <span className="rounded bg-[#0F172A] px-1.5 text-xs font-bold text-[#FFFFFF]">
              Playing
            </span>
          ) : progress?.completed ? (
            <span className="text-xs font-semibold text-[#3F7A0A]">Done</span>
          ) : pct > 0 ? (
            <span className="text-xs text-muted-foreground">{pct}% watched</span>
          ) : null}
        </span>
        <span className="block text-sm font-semibold leading-snug text-foreground">
          {lesson.title}
        </span>
        <span className="mt-0.5 hidden text-xs leading-relaxed text-muted-foreground sm:line-clamp-2">
          {lesson.description}
        </span>
      </span>
      <ChevronRight className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
    </button>
  )
}

export default Academy
